import React, { useEffect, useState } from 'react';
import { Spinner, Toast } from '../../lib';
import apiServices from '../../services/apiServices';
import {
  DashboardVenueRow,
  GetSupportDashboardSummary,
  GetSupportDashboardWageByVenue,
  SupportDashboardSummary,
} from '../../services/dtos';
import {
  TICKET_QUEUES,
  fmtDateTime,
  ticketCategoryLabel,
} from './supportShared';

// S1 leadership dashboard (vision Feature 6): "ticket counts, categories,
// average time to resolve, which venues generate the most wage issues."
// Numbers only — the server's response shapes carry no ticket ids, subjects,
// or member names, which is what lets confidential tickets be COUNTED here
// (the callout says how many) without ever being itemized.

type Window = { days?: number; label: string };
const WINDOWS: Window[] = [
  { label: 'All time' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
];

const STATUS_LABELS: Record<string, string> = {
  New: 'New',
  InProgress: 'In progress',
  Resolved: 'Resolved',
  Dismissed: 'Dismissed',
};

const fmtDays = (d?: number) => (d == null ? '—' : `${d}d`);

const LeadershipDashboardPage: React.FC = () => {
  const [summary, setSummary] = useState<SupportDashboardSummary | null>(null);
  const [venues, setVenues] = useState<DashboardVenueRow[] | null>(null);
  const [days, setDays] = useState<number | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSummary(null);
    setVenues(null);
    setError(null);
    Promise.all([
      apiServices.client.get(new GetSupportDashboardSummary({ days })),
      apiServices.client.get(new GetSupportDashboardWageByVenue({ days })),
    ])
      .then(([s, v]) => {
        setSummary(s);
        setVenues(v);
      })
      .catch((e: any) =>
        setError(e?.responseStatus?.message || 'Loading the dashboard failed — try again.')
      );
  }, [days]);

  const awaiting =
    summary?.byQueue?.reduce((n, q) => n + (q.awaitingStaff || 0), 0) ?? 0;

  return (
    <div className="mx-auto w-full max-w-5xl flex flex-col gap-6 dark:text-white">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Leadership Dashboard</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Ticket volume, response times, and wage-issue patterns — counts
            only, never individual tickets.
          </p>
        </div>
        <div className="flex gap-1 text-xs font-medium">
          {WINDOWS.map((w) => (
            <button
              key={w.label}
              onClick={() => setDays(w.days)}
              className={`rounded px-2.5 py-1.5 ${
                days === w.days
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {error && <Toast color="failure">{error}</Toast>}
      {!summary && !error && <Spinner color="info" />}

      {summary && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Total requests" value={summary.totalTickets} />
            <StatCard label="Open" value={summary.openTickets} />
            <StatCard label="Waiting on staff" value={awaiting} />
            <StatCard
              label="Avg time to resolve"
              value={fmtDays(summary.resolution?.avgDays)}
              sub={
                summary.resolution?.resolvedCount
                  ? `${summary.resolution.resolvedCount} resolved · median ${fmtDays(
                      summary.resolution.medianDays
                    )}`
                  : 'nothing resolved yet'
              }
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            {summary.byStatus?.map((s) => (
              <span
                key={s.status}
                className="rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-600 dark:text-gray-300"
              >
                {STATUS_LABELS[s.status || ''] || s.status}: {s.count}
              </span>
            ))}
          </div>

          {(summary.confidentialTickets ?? 0) > 0 && (
            <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900 p-3 text-sm text-purple-800 dark:text-purple-200">
              {summary.confidentialTickets} confidential ticket
              {summary.confidentialTickets === 1 ? ' is' : 's are'} included in
              these counts. Confidential tickets never appear individually here
              — only the leader they route to can open them.
            </div>
          )}

          <Section title="By category">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-3 py-2">Category</th>
                  <th className="px-3 py-2 w-24">Total</th>
                  <th className="px-3 py-2 w-24">Open</th>
                  <th className="px-3 py-2 w-40">Avg time to resolve</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
                {summary.byCategory?.map((c) => (
                  <tr key={c.category}>
                    <td className="px-3 py-1.5 text-gray-900 dark:text-white">
                      {ticketCategoryLabel(c.category)}
                    </td>
                    <td className="px-3 py-1.5">{c.total}</td>
                    <td className="px-3 py-1.5">{c.open}</td>
                    <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">
                      {fmtDays(c.avgResolutionDays)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Open tickets by queue">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-3 py-2">Queue</th>
                  <th className="px-3 py-2 w-24">Open</th>
                  <th className="px-3 py-2 w-36">Waiting on staff</th>
                  <th className="px-3 py-2 w-28">Oldest</th>
                  <th className="px-3 py-2 w-28">Avg age</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
                {summary.byQueue?.map((q) => (
                  <tr key={q.queue}>
                    <td className="px-3 py-1.5 text-gray-900 dark:text-white">
                      {TICKET_QUEUES.find((x) => x.name === q.queue)?.label || q.queue}
                    </td>
                    <td className="px-3 py-1.5">{q.open}</td>
                    <td className="px-3 py-1.5">
                      {(q.awaitingStaff ?? 0) > 0 ? (
                        <span className="font-medium text-amber-700 dark:text-amber-400">
                          {q.awaitingStaff}
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">0</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">
                      {fmtDays(q.oldestOpenDays)}
                    </td>
                    <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">
                      {fmtDays(q.avgOpenDays)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Wage issues by venue">
            {venues && venues.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                No wage tickets{days ? ' in this window' : ''} yet.
              </div>
            ) : (
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-3 py-2">Venue</th>
                    <th className="px-3 py-2 w-24">Tickets</th>
                    <th className="px-3 py-2 w-24">Open</th>
                    <th className="px-3 py-2 w-44">Most recent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
                  {venues?.map((v, i) => (
                    <tr key={v.employerId ?? `none-${i}`}>
                      <td className="px-3 py-1.5 text-gray-900 dark:text-white break-words">
                        {v.site}
                        {v.employerId != null && (
                          <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                            #{v.employerId}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5">{v.total}</td>
                      <td className="px-3 py-1.5">{v.open}</td>
                      <td className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400">
                        {fmtDateTime(v.lastTicketAtUtc)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
            <span>
              Ticket bans: {summary.bans?.activeBans ?? 0} active,{' '}
              {summary.bans?.totalIssued ?? 0} ever issued (all time)
            </span>
            <span>Generated {fmtDateTime(summary.generatedAtUtc)}</span>
          </div>
        </>
      )}
    </div>
  );
};

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      {sub && (
        <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">{sub}</div>
      )}
    </div>
  );
}

function Section({ title, children }: React.PropsWithChildren<{ title: string }>) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
        {title}
      </h3>
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        {children}
      </div>
    </div>
  );
}

export default LeadershipDashboardPage;
